import {Component, OnInit} from "@angular/core";
import {Exam} from "../../../shared/models/exam";
import {ExamService} from "../../../shared/services/exam/exam.service";
import {debounceTime, take} from "rxjs/operators";
import {FormControl} from "@angular/forms";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {ExamModalComponent} from "../exam-modal/exam-modal.component";
import {environment} from "../../../../environments/environment";
import {ExamResultFileModalComponent} from "../exam-result-file-modal/exam-result-file-modal.component";
import {ExamResetConfirmModalComponent} from "../exam-reset-confirm-modal/exam-reset-confirm-modal.component";
import {RequestExamsModalComponent} from "../request-exams-modal/request-exams-modal.component";
import {VoucherRequestsModalComponent} from "../voucher-requests-modal/voucher-requests-modal.component";
import {SelectModalComponent} from "../../select-modal/select-modal.component";
import {UserService} from "../../../shared/services/user/user.service";
import {GroupsEnum} from "../../../shared/enums/groupsEnum";
import {User} from "../../../shared/models/user";
import {CertificationService} from "../../../shared/services/certification/certification.service";
import {Certification} from "../../../shared/models/certification";
import {ExamSessionService} from "../../../shared/services/exam-session/exam-session.service";
import {ShortExamSession} from "../../../shared/models/examSession";
import {CorrectionModalComponent} from "../../correction/correction-modal/correction-modal.component";
import {ExamFilterEnum} from "../../../shared/enums/examFilterEnum";
import {UserSelectModalComponent} from "../../user/user-select-modal/user-select-modal.component";
import { ExamHistoryModalComponent } from "../exam-history-modal/exam-history-modal.component";
import {SelectFileModalComponent} from "../../select-file-modal/select-file-modal.component";
import {ExamCommentsModalComponent} from "../exam-comments-modal/exam-comments-modal.component";
import {ExamEditionModalComponent} from "../exam-edition-modal/exam-edition-modal.component";
import {ExamRescheduleModalComponent} from "../exam-reschedule-modal/exam-reschedule-modal.component";
import {InvoiceService} from "../../../shared/services/invoice/invoice.service";
import {VoucherRequestService} from "../../../shared/services/voucher-request/voucher-request.service";
import {VoucherRequest} from "../../../shared/models/voucherRequest";
import {FileDownloadHelper} from "../../../shared/helpers/file-download.helper";
import {HttpResponse} from "@angular/common/http";
import {ToastrService} from "ngx-toastr";
import {ExamResultsModalComponent} from "../../exam/exam-results-modal/exam-results-modal.component";
import {UserModalComponent} from "../../user/user-modal/user-modal.component";
import {SessionEditModalComponent} from "../../session/session-edit-modal/session-edit-modal.component";
import {ActivatedRoute} from "@angular/router";


@Component({
    "selector": "app-exam-list",
    "templateUrl": "./exam-list.component.html",
    "styleUrls": ["./exam-list.component.scss"]
})
export class ExamListComponent implements OnInit {

    // List of all the exams fetched from backend
    exams: Array<Exam>;

    searchTermControl: FormControl;

    studentFilterControl: FormControl;

    sessionFilterControl: FormControl;

    correctorFilterControl: FormControl;

    baseUrl = environment.baseUrl;

    sessions: Array<ShortExamSession>;

    user: User;

    loading: boolean;

    // Pagniation related
    page: number;

    pageSize: number;

    itemsCount: number;

    // Counts by status (for filter chips)
    statusCounts: {total: number, free: number, waiting: number, assigned: number, to_check: number, to_correct: number, to_validate: number, closed: number} = {
        total: 0,
        free: 0,
        waiting: 0,
        assigned: 0,
        to_check: 0,
        to_correct: 0,
        to_validate: 0,
        closed: 0
    };

    retrievingInvoiceDocumentForExamId: number = null;

    // Demandes de tickets en attente du responsable (null tant que non chargé)
    pendingVoucherRequestsCount: number = null;

    constructor (
        private examService: ExamService,
        private certificationService: CertificationService,
        private sessionService: ExamSessionService,
        private userService: UserService,
        private modalService: NgbModal,
        private invoiceService: InvoiceService,
        private voucherRequestService: VoucherRequestService,
        private toastr: ToastrService,
        private route: ActivatedRoute
    ) {

        this.loading = false;
        this.page = 1;
        this.pageSize = environment.pageSize;
        this.searchTermControl = new FormControl("");
        this.studentFilterControl = new FormControl(ExamFilterEnum.ALL);
        this.sessionFilterControl = new FormControl("all");
        this.correctorFilterControl = new FormControl("corrector_all");
    }

    ngOnInit () {

        // Filtre de session passé dans l'URL (ex. depuis l'onglet « Sessions »).
        // Lu avant les abonnements aux valueChanges pour que le setValue ne
        // déclenche pas une salve de requêtes en double.
        this.route.queryParams.pipe(take(1)).subscribe((params) => {
            const sessionId = params["session_id"];
            if (sessionId) {
                this.sessionFilterControl.setValue(sessionId);
            }
        });

        // Charger les sessions
        this.sessionService.shortList().pipe(take(1))
            .subscribe((sessions: Array<ShortExamSession>) => {
                this.sessions = sessions;
            });

        // Subscribe to the change of value of the search field
        this.searchTermControl.valueChanges.pipe(debounceTime(1000)).
            subscribe(() => {
                this.page = 1;
                this.fetchStatusCounts();
                this.fetchItemCount();
                this.fetchCurrentPage();
            });

        // Subscribe to the change of value of the candidate filter
        this.studentFilterControl.valueChanges.
            subscribe(() => {
                this.page = 1;
                this.fetchStatusCounts();
                this.fetchItemCount();
                this.fetchCurrentPage();
            });

        // Subscribe to the change of value of the session filter
        this.sessionFilterControl.valueChanges.
            subscribe(() => {
                this.page = 1;
                this.fetchStatusCounts();
                this.fetchItemCount();
                this.fetchCurrentPage();
            });

        // Subscribe to the change of value of the corrector filter
        this.correctorFilterControl.valueChanges.
            subscribe(() => {
                this.page = 1;
                this.fetchStatusCounts();
                this.fetchItemCount();
                this.fetchCurrentPage();
            });

        this.fetchStatusCounts();
        this.fetchItemCount();
        this.fetchCurrentPage();

        this.userService.getMe().pipe(take(1)).
            subscribe((user: User) => {

                this.user = user;

                if (this.user.isExamCenterBoss) this.fetchPendingVoucherRequestsCount();

            });

    }

    fetchCurrentPage () {
        this.loading = true;

        const searchTerm = this.searchTermControl.value;
        const studentFilter = this.studentFilterControl.value;
        const sessionFilter = this.sessionFilterControl.value;
        const correctorFilter = this.correctorFilterControl.value;

        this.examService.list(
            this.page,
            searchTerm,
            [studentFilter, correctorFilter],
            sessionFilter != "all" ? sessionFilter : null
        ).pipe(take(1)).
            subscribe((res: Array<Exam>) => {
                this.exams = res;
                this.loading = false;

            });

    }

    fetchStatusCounts () {
        const term = this.searchTermControl.value;
        const sessionFilter = this.sessionFilterControl.value;

        this.examService.countByStatus(
            term,
            sessionFilter !== "all" ? sessionFilter : null
        ).pipe(take(1)).
            subscribe((counts) => {
                this.statusCounts = counts;
            });
    }

    fetchItemCount () {
        const searchTerm = this.searchTermControl.value;
        const studentFilter = this.studentFilterControl.value;
        const correctorFilter = this.correctorFilterControl.value;
        const sessionFilter = this.sessionFilterControl.value;

        this.examService.count(
            searchTerm,
            [studentFilter, correctorFilter],
            sessionFilter != "all" ? sessionFilter : null
        ).pipe(take(1)).
            subscribe((count: number) => {
                this.itemsCount = count;
            });

    }

    setStudentFilter (filter: string) {
        this.studentFilterControl.setValue(filter);
    }

    onPageChange (newPage: number) {
        this.page = newPage;
        this.fetchCurrentPage();

    }

    add () {

        const modalRef = this.modalService.open(
            ExamModalComponent,
            {"size": "lg"}
        );

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        modalRef.result.then((res: Array<Exam>) => {

            this.fetchCurrentPage();
            this.fetchItemCount();

        }).catch(() => {
        // Dummy catch to prevent error on close
        });

    }

    request () {

        const modalRef = this.modalService.open(
            RequestExamsModalComponent,
            {"size": "lg"}
        );
        modalRef.componentInstance.examCenters = this.user.examCenters;

        modalRef.result.then(() => {

            this.fetchPendingVoucherRequestsCount();

        }).catch(() => {
        // Dummy catch to prevent error on close
        });

    }

    /*
     * Le back restreint déjà la liste aux centres du responsable : on compte
     * simplement ce qu'il renvoie pour le statut « en attente ».
     */
    fetchPendingVoucherRequestsCount () {

        this.voucherRequestService.list({"status": "pending"}).pipe(take(1)).
            subscribe((requests: Array<VoucherRequest>) => {
                this.pendingVoucherRequestsCount = requests.length;
            });

    }

    get voucherRequestsLabel (): string {

        if (this.pendingVoucherRequestsCount === null) return "Voir mes demandes de tickets";

        if (this.pendingVoucherRequestsCount === 0) return "Aucune demande en attente";

        if (this.pendingVoucherRequestsCount === 1) return "1 demande en attente";

        return `${this.pendingVoucherRequestsCount} demandes en attente`;

    }

    get voucherRequestsLabelClass (): string {

        return this.pendingVoucherRequestsCount > 0 ? "text-warning" : "text-muted";

    }

    openVoucherRequestsModal () {

        this.modalService.open(
            VoucherRequestsModalComponent,
            {"size": "lg"}
        );

    }

    openFilesModal (exam: Exam) {

        const modalRef = this.modalService.open(
            ExamResultFileModalComponent,
            {"size": "lg"}
        );
        modalRef.componentInstance.exam = exam;
        modalRef.result.finally(() => {

            /*
             * This could be done in a cleaner way.
             * there's not need to refetch the page if no document was added,
             * but it's not that trivial to do
             */
            this.fetchCurrentPage();

        });

    }

    openCorrectionModal (exam: Exam) {

        const modalRef = this.modalService.open(
            CorrectionModalComponent,
            {"size": "lg"}
        );
        modalRef.componentInstance.exam = exam;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        modalRef.result.then((res: Exam) => {

            this.fetchCurrentPage();

        }).catch(() => {
        // Dummy catch to prevent error on close
        });

    }

    openExamResultsModal (exam: Exam) {
        const modalRef = this.modalService.open(
            ExamResultsModalComponent,
            {"size": "lg"}
        );
        modalRef.componentInstance.exam = exam;
    }

    openSelectStudentModal (exam: Exam) {

        const modalRef = this.modalService.open(
            UserSelectModalComponent,
            {"size": "xl"}
        );
        modalRef.componentInstance.group = GroupsEnum.STUDENT;
        modalRef.result.then((student: User) => {

            this.examService.partialUpdate(
                exam.id,
                {
                    "student_id": student.id
                }
            ).pipe(take(1)).

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                subscribe((res: Exam) => {

                    this.fetchCurrentPage();

                });

        }).catch(() => {
        // Dummy catch to prevent error on close
        });

    }

    openExamHistoryModal (exam: Exam) {
        const modalRef = this.modalService.open(
            ExamHistoryModalComponent,
            {"size": "lg"}
        );
        modalRef.componentInstance.exam = exam;
    }

    openSelectCertificationModal (exam: Exam) {

        this.certificationService.shortListUnarchived().pipe(take(1)).
            subscribe((certifications: Array<Certification>) => {

                const modalRef = this.modalService.open(
                    SelectModalComponent,
                    {"size": "lg"}
                );
                modalRef.componentInstance.title = "Sélectionnez une certification";
                modalRef.componentInstance.options = certifications;
                modalRef.componentInstance.defaultOption = exam.certification;
                modalRef.result.then((certification: Certification) => {

                    this.examService.partialUpdate(
                        exam.id,
                        {"certification_id": certification.id}
                    ).pipe(take(1)).

                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        subscribe((res: Exam) => {

                            this.fetchCurrentPage();

                        });

                }).catch(() => {
                // Dummy catch to prevent error on close
                });

            });

    }

    openSelectSessionModal (exam: Exam) {

        this.sessionService.shortList(
            exam.exam_center.id, true
        ).pipe(take(1)).
            subscribe((sessions: Array<ShortExamSession>) => {

                const modalRef = this.modalService.open(
                    SelectModalComponent,
                    {"size": "lg"}
                );
                modalRef.componentInstance.title = "Sélectionnez une session";
                modalRef.componentInstance.options = sessions;
                modalRef.componentInstance.defaultOption = exam.session;
                modalRef.result.then((session: ShortExamSession) => {

                    this.examService.partialUpdate(
                        exam.id,
                        {
                            "session_id": session.id
                        }
                    ).pipe(take(1)).

                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        subscribe((res: Exam) => {
                            this.fetchCurrentPage();
                        });

                }).catch(() => {
                // Dummy catch to prevent error on close
                });

            });

    }

    openDiplomeModal (exam: Exam) {

        const modalRef = this.modalService.open(
            SelectFileModalComponent,
            {"size": "lg"}
        );
        modalRef.componentInstance.title = "Ajouter le fichier du diplôme";
        modalRef.result.then((file: File) => {

            this.examService.uploadDiplome(
                exam.id,
                file
            ).pipe(take(1)).

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                subscribe((res: Exam) => {

                    this.fetchCurrentPage();

                });

        }).catch(() => {
        // Dummy catch to prevent error on close
        });

    }

    openExamResetConfirmModal (exam: Exam) {

        const modalRef = this.modalService.open(
            ExamResetConfirmModalComponent,
            {"size": "lg"}
        );
        modalRef.componentInstance.exam = exam;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        modalRef.result.then((res: Exam) => {

            this.fetchCurrentPage();

        }).catch(() => {
            // Dummy catch to avoid ugly errors in console
        });

    }

    openExamCommentsModal (exam: Exam) {

        const modalRef = this.modalService.open(
            ExamCommentsModalComponent,
            {"size": "lg"}
        );
        modalRef.componentInstance.exam = exam;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        modalRef.result.then((res: Exam) => {

            this.fetchCurrentPage();

        }).catch(() => {
            // Dummy catch to avoid ugly errors in console
        });

    }

    edit (exam: Exam) {
        const modalRef = this.modalService.open(
            ExamEditionModalComponent,
            {size: "lg"}
        );
        modalRef.componentInstance.exam = exam;
        modalRef.componentInstance.canEditProfessional =
            this.user.isStaff || this.user.isExamCenterBoss;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        modalRef.result.then((res: Exam) => {
            this.fetchCurrentPage();
        }).catch(() => {
            // Dummy catch to avoid ugly errors in console
        });
    }

    reschedule (exam: Exam) {
        const modalRef = this.modalService.open(
            ExamRescheduleModalComponent,
            {size: "lg"}
        );
        modalRef.componentInstance.exam = exam;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        modalRef.result.then((res: Exam) => {
            // Le statut du ticket peut changer (confirmation annulée, convocation envoyée)
            this.fetchCurrentPage();
            this.fetchStatusCounts();
        }).catch(() => {
            // Dummy catch to avoid ugly errors in console
        });
    }

    showInvoiceDocument (exam: Exam) {
        this.retrievingInvoiceDocumentForExamId = exam.id;

        this.invoiceService.retrieveFile(exam.invoice.id).pipe(take(1)).
            subscribe(
                (response: HttpResponse<Blob>) => {
                    this.retrievingInvoiceDocumentForExamId = null;
                    FileDownloadHelper.downloadBlobFromResponse(response);
                },
                () => {
                    this.retrievingInvoiceDocumentForExamId = null;
                    this.toastr.error(
                        "Contactez l'administrateur pour plus d'informations",
                        "Fichier introuvable"
                    );
                }
            );
    }

    certificatePdf (exam: Exam) {
        this.examService.certificatePdf(exam.id).pipe(take(1)).
            subscribe(
                (response: HttpResponse<Blob>) => {
                    FileDownloadHelper.downloadBlobFromResponse(
                        response,
                        `attestation_${exam.voucher_code}.pdf`
                    );
                },
                () => {
                    this.toastr.error(
                        "Contactez l'administrateur pour plus d'informations",
                        "Erreur"
                    );
                }
            );
    }

    openStudentModal (exam: Exam) {
        const student = exam.student;
        const canEdit = exam.invoice.center.boss_id == this.user.id;

        const modalRef = this.modalService.open(
            UserModalComponent,
            {size: "lg"}
        );
        modalRef.componentInstance.inputUser = student;
        modalRef.componentInstance.readOnly = !canEdit;
        modalRef.result.then(() => {
        this.fetchCurrentPage();
        
        }).catch((err) => {
        // Catching to prevent an error shown in the console
        });
    }


    openSessionModal (session) {
        const modalRef = this.modalService.open(
            SessionEditModalComponent,
            {size: "lg"}
        );
        modalRef.componentInstance.examSession = session;
        modalRef.result.then(() => {
            
        }).catch(() => {
        // Dummy catch tu prevent exception thrown on modal dismissal
        });

    }
    

}
